import type { MouseEvent, PropsWithChildren } from 'react';
import { useRef } from 'react';

import { withButtonAlignment } from '@skyscanner-internal/backpack-web/bpk-component-icon';
import BpkIconAlert from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/alert--active';
import BpkIconCalendar from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/calendar';
import BpkIconCars from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/cars';
import BpkIconChart from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/chart';
import BpkIconFlight from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/flight';
import BpkIconFlightFlexible from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/flight-flexible';
import BpkIconHotelFlexible from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/hotel-flexible';
import BpkIconHotels from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/hotels';
import BpkIconInsurance from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/insurance';
import BpkIconMileage from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/mileage';
import BpkIconMoney from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/money';
import BpkIconPriceTag from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/price-tag';
import BpkIconSearch from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/search';
import BpkIconWallet from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/wallet';
import InformationCircleIconSm from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/information-circle';
import BpkLink from '@skyscanner-internal/backpack-web/bpk-component-link';
import BpkMobileScrollContainer from '@skyscanner-internal/backpack-web/bpk-component-mobile-scroll-container';
import BpkPanel from '@skyscanner-internal/backpack-web/bpk-component-panel';
import BpkText, {
  TEXT_STYLES,
} from '@skyscanner-internal/backpack-web/bpk-component-text';
import BpkTooltip from '@skyscanner-internal/backpack-web/bpk-component-tooltip';
import { redirect } from 'saddlebag-browser';

import { textColors } from '@skyscanner/bpk-foundations-web/tokens/base.es6';

import { anchorScroll } from '../../utils';
import { useMBDContext } from '../common/MBD/useMBDContext';

import type {
  ConfidenceMessagingProps,
  ToolTip,
} from '@skyscanner-internal/landing-pages-shared-types/types-shared/ConfidenceMessaging';

import STYLES from './ConfidenceMessagingV2.module.scss';

type ConfidenceMessagingIcon =
  ConfidenceMessagingProps['dataList'][number]['icon'];

const TooltipIcon = withButtonAlignment(InformationCircleIconSm);

const LINK_TAG_PATTERN = /<link href="([^"]+)">(.*?)<\/link>/;

const getIconComponent = (icon: ConfidenceMessagingIcon) => {
  switch (icon) {
    case 'flight':
      return BpkIconFlight;
    case 'cars':
      return BpkIconCars;
    case 'hotels':
      return BpkIconHotels;
    case 'calendar':
      return BpkIconCalendar;
    case 'price-tag':
      return BpkIconPriceTag;
    case 'money':
      return BpkIconMoney;
    case 'mileage':
      return BpkIconMileage;
    case 'wallet':
      return BpkIconWallet;
    case 'search':
      return BpkIconSearch;
    case 'hotel-flexible':
      return BpkIconHotelFlexible;
    case 'alert':
      return BpkIconAlert;
    case 'chart':
      return BpkIconChart;
    case 'flight-flexible':
      return BpkIconFlightFlexible;
    case 'insurance':
      return BpkIconInsurance;
    default:
      throw Error(`Icon component not defined for icon name "${icon}"`);
  }
};

const Tooltip = ({
  children = null,
  description,
}: PropsWithChildren<{
  description: ToolTip['description'];
}>) => {
  const iconRef = useRef(null);
  return (
    <BpkTooltip
      id="best-tooltip"
      ariaLabel={description}
      target={
        <span ref={iconRef} role="tooltip" data-testid="best-tooltip-wrapper">
          {children}
        </span>
      }
    >
      <BpkText
        tagName="h5"
        className={STYLES.tooltipHeader}
        textStyle={TEXT_STYLES.heading5}
      />
      <div className={STYLES.tooltipParagraph}>
        <BpkText tagName="p" textStyle={TEXT_STYLES.footnote} key={description}>
          {description}
        </BpkText>
      </div>
    </BpkTooltip>
  );
};

const ConfidenceMessagingV2 = (props: ConfidenceMessagingProps) => {
  const { buildMBDParam, rawComponentName } = useMBDContext();

  if (!props?.dataList?.length) {
    return null;
  }

  const { dataList } = props;

  // Handle clicks anywhere inside the link content, falling back to a secondary URL
  // when the anchor target is not present on the page.
  const onContentClick = (e: MouseEvent, fallbackUrl?: string) => {
    const target = e.target as HTMLElement;
    const link = target.closest('a');
    if (link?.href) {
      if (link.hash?.length) {
        const scrolled = anchorScroll(link.hash.replace('#', ''));
        if (!scrolled && fallbackUrl) {
          redirect(fallbackUrl);
        }
      } else if (link.target === '_blank') {
        redirect(link.href, true);
      } else {
        redirect(link.href);
      }
    }
  };

  return (
    <BpkMobileScrollContainer
      className={STYLES.ConfidenceMessagingV2}
      data-testid="confidenceMessagingV2"
      data-tracking-element-id={rawComponentName}
      data-tracking-common-params={buildMBDParam(rawComponentName, {
        version: 'v2',
      })}
    >
      <div className={STYLES.container}>
        {dataList.map(
          (
            { description, fallbackUrl, icon, linkText, linkUrl, toolTip },
            index,
          ) => {
            const IconComponent = getIconComponent(icon);

            const tagMatch = description.match(LINK_TAG_PATTERN);
            const hasInlineLink = !!tagMatch;

            const renderLink = (content?: string, href?: string) => (
              <BpkLink
                rel="nofollow"
                data-tracking-element-id={`${rawComponentName}_item_link_${index}`}
                data-tracking-common-params={buildMBDParam(
                  `${rawComponentName}_item_link`,
                  {
                    version: 'v2',
                    link_type: icon,
                    index: index.toString(),
                  },
                )}
                href={href ?? linkUrl}
                onClick={(e: MouseEvent) => {
                  e.preventDefault();
                  onContentClick(e, fallbackUrl);
                }}
              >
                {content ?? linkText}
              </BpkLink>
            );

            return (
              <BpkPanel
                key={description}
                className={STYLES.item}
                data-tracking-element-id={`${rawComponentName}_item_${index}`}
                data-tracking-common-params={buildMBDParam(
                  `${rawComponentName}_item`,
                  {
                    version: 'v2',
                  },
                )}
              >
                <div className={STYLES.icon}>
                  <IconComponent />
                </div>

                <div className={STYLES.toolTip}>
                  {toolTip && (
                    <Tooltip description={toolTip.description}>
                      <TooltipIcon fill={textColors.textSecondaryDay} />
                    </Tooltip>
                  )}
                </div>

                <BpkText
                  textStyle={TEXT_STYLES.heading4}
                  className={STYLES.description}
                  tagName="p"
                >
                  {hasInlineLink && tagMatch ? (
                    <>
                      {description.slice(0, tagMatch.index)}
                      {renderLink(tagMatch[2], tagMatch[1])}
                      {description.slice(
                        (tagMatch.index as number) + tagMatch[0].length,
                      )}
                    </>
                  ) : (
                    description
                  )}
                </BpkText>

                {!hasInlineLink && linkText && linkUrl && (
                  <BpkText
                    textStyle={TEXT_STYLES.link}
                    className={STYLES.description}
                    tagName="p"
                  >
                    {renderLink()}
                  </BpkText>
                )}
              </BpkPanel>
            );
          },
        )}
      </div>
    </BpkMobileScrollContainer>
  );
};

export default ConfidenceMessagingV2;
